'use client';

import { Box, Button, Flex, Logo } from '@ironhack/design-system2/components';
import { ArrowRight } from '@ironhack/design-system2/icons';
import { isEmpty, keys, toPairs } from 'ramda';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { setCookie } from 'cookies-next';

import {
  getPageUrl,
  isPageIndexable,
  splitUrlRegion,
  urlToRouteMapping,
} from '@/app/urls/url-mapping';
import type { Language, Region } from '@/types';
import {
  regionCodesToEnglishNames,
  regionToLanguages,
  urlToDatoLocale,
} from '@/lib/utils';
import { type DatoBlock, type DatoButtonBlock } from '@/lib/datocms';
import { SelectField } from '@/app/components/client/select-field';
import { CheckboxField } from '@/app/components/client/checkbox-field';

type FormData = {
  region: string;
  language: string;
  rememberMySelection: boolean;
};

export type RootModule = DatoBlock<{
  regionSelectorForm: {
    regionPlaceholder: string;
    languagePlaceholder: string;
    rememberMySelection: string;
  };
  goButton: [DatoButtonBlock];
}>;

type Props = { data: RootModule };

export const RootModule = (props: Props) => {
  const {
    data: {
      regionSelectorForm: {
        languagePlaceholder,
        regionPlaceholder,
        rememberMySelection,
      },
      goButton: [{ text: goButtonText }],
    },
  } = props;

  const formMethods = useForm<FormData>({
    defaultValues: {
      region: 'ww',
      language: 'en',
      rememberMySelection: true,
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });
  const {
    formState: { errors, isSubmitSuccessful, isSubmitted, isSubmitting },
    handleSubmit,
    watch,
    resetField,
  } = formMethods;

  const regions = useMemo(
    () =>
      toPairs(regionToLanguages).map(([region, [language]]) => {
        const code = urlToDatoLocale[`${region}${language}`].replace('_', '-');
        const parsedRegion = code.split('-')[1];

        const regionDisplayName = new Intl.DisplayNames([code], {
          type: 'region',
        });
        const regionText = regionDisplayName.of(parsedRegion) ?? '';
        const enText = regionCodesToEnglishNames[region];
        return {
          key: region,
          text:
            region === 'ww'
              ? regionCodesToEnglishNames.ww
              : enText === regionText
              ? regionText
              : `${enText} / ${regionText}`,
        };
      }),
    []
  );

  const selectedRegion = watch('region');

  const languageOptions = useMemo(
    () =>
      selectedRegion
        ? regionToLanguages[selectedRegion as Region].map((language) => {
            const code = urlToDatoLocale[
              `${selectedRegion}${language}`
            ].replace('_', '-');
            const displayName = new Intl.DisplayNames([code], {
              type: 'language',
            });
            const parsedLanguage = displayName.of(language) ?? '';
            return {
              key: language,
              text: parsedLanguage,
            };
          })
        : [],
    [selectedRegion]
  );

  const regionsHiddenLinks = useMemo(
    () =>
      keys(urlToRouteMapping)
        .map(splitUrlRegion)
        .filter((params) => isPageIndexable('home', params))
        .map((params) => ({
          href: getPageUrl('home', params),
          region: params.region,
        })),
    []
  );

  useEffect(() => {
    if (languageOptions.length > 0) {
      resetField('language', { defaultValue: languageOptions[0].key });
    }
  }, [languageOptions, resetField]);

  // Set the initial value of region based on user's browser language
  useEffect(() => {
    for (const browserLanguage of navigator.languages) {
      const [language, region] = browserLanguage
        .replace('GB', 'uk')
        .toLowerCase()
        .split('-');

      if (region && regionToLanguages[region as Region]) {
        resetField('region', { defaultValue: region });
        return;
      }

      if (regionToLanguages[language as Region]) {
        resetField('region', { defaultValue: language });
        return;
      }
    }
  }, [resetField]);

  const router = useRouter();

  const onSubmit = (formData: FormData) => {
    const path = getPageUrl('home', {
      region: formData.region as Region,
      language: formData.language as Language,
    });

    if (!path) {
      return;
    }

    if (formData.rememberMySelection) {
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      setCookie('user_preference_region', path, { expires });
    }

    router.push(path);
  };

  return (
    <>
      <Logo
        colorScheme="light"
        height={['30px', null, '40px']}
        width={['200px', null, '250px']}
      />
      <FormProvider {...formMethods}>
        <Flex
          as="form"
          bg="fill.white"
          borderRadius="lg"
          color="text.secondary"
          direction={['column', null, 'row']}
          gap={2}
          marginBottom={[0, null, 'auto']}
          marginTop="auto"
          maxW={1120}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          padding={2}
          w="full"
        >
          <SelectField
            color="text.secondary"
            flex={1}
            isRequired
            name="region"
            options={regions}
            placeholder={regionPlaceholder}
          />
          <SelectField
            color="text.secondary"
            flex={1}
            isRequired
            name="language"
            options={languageOptions}
            placeholder={languagePlaceholder}
            textTransform="capitalize"
          />
          <CheckboxField
            color="text.primary"
            display="flex"
            isRequired={false}
            label={rememberMySelection}
            name="rememberMySelection"
            w="fit-content"
          />
          <Button
            flex={1}
            isDisabled={isSubmitted && !isEmpty(errors)}
            isLoading={isSubmitting || isSubmitSuccessful}
            px={0}
            rightIcon={ArrowRight}
            type="submit"
          >
            {goButtonText}
          </Button>
        </Flex>
      </FormProvider>
      <Box hidden>
        <ul>
          {regionsHiddenLinks.map((link) => (
            <li key={link.href}>
              <a href={link.href}>
                {`Ironhack in ${regionCodesToEnglishNames[link.region]}`}
              </a>
            </li>
          ))}
        </ul>
      </Box>
    </>
  );
};
